import { useContext } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { get } from 'lodash';
import { translate } from 'javascripts/translations/i18n';

import { Steven } from 'helpers/steven';
import { graphQLIdDecoder } from 'helpers/graphql-utils';
import { getGqlErrors } from 'components/projects/taskList/helpers';
import TASK_MARK_COMPLETE from 'components/projects/taskList/gql/taskMarkComplete.graphql';
import TASK_MARK_INCOMPLETE from 'components/projects/taskList/gql/taskMarkIncomplete.graphql';
import GET_TASK_LIST_HEADER_INFO from 'components/projects/taskList/gql/getTaskListHeaderInfo.graphql';
import TemplateContext from 'components/projects/taskList/templates/TemplateContext';
import toast from 'helpers/toast';

type useTaskStatusProps = {
  taskId: string;
  taskListId: string;
  skipOptimisticUpdate?: boolean;
};

const useTaskStatus = ({
  taskId,
  taskListId,
  skipOptimisticUpdate = false,
}: useTaskStatusProps) => {
  const { template } = useContext(TemplateContext) || {};
  const { cache } = useApolloClient();

  const showErrorToast = (errorMsg: string) => {
    toast.error(errorMsg);
  };

  const tellSteven = ({
    updatedAction,
  }: {
    updatedAction: 'completed' | 'incomplete';
  }) =>
    Steven.entityCrud({
      entity_type: 'checklist_field',
      entity_id: `${graphQLIdDecoder(taskId).databaseId}`,
      action: 'updated',
      updated_action:
        updatedAction === 'completed'
          ? 'status_marked_completed'
          : 'status_marked_incomplete',
    });

  const [markTaskIncompleteMutation] = useMutation(TASK_MARK_INCOMPLETE, {
    onCompleted: (data) => {
      const gqlErrors = getGqlErrors(data);

      if (gqlErrors.length) {
        showErrorToast(gqlErrors[0].message);
        return;
      }
      tellSteven({
        updatedAction: 'incomplete',
      });
    },
    optimisticResponse: () => {
      const { viewer } = cache.readQuery({
        query: GET_TASK_LIST_HEADER_INFO,
        variables: {
          id: taskListId,
        },
      }) as any;

      const tasksCount = get(viewer, 'taskList.tasksCount', 0);
      const tasksCompletedCount = get(
        viewer,
        'taskList.tasksCompletedCount',
        -1,
      );

      return {
        __typename: 'Mutation',
        taskMarkIncomplete: {
          __typename: 'TaskMarkIncompletePayload',
          task: {
            id: taskId,
            __typename: 'Task',
            completed: false,
          },
          taskList: {
            __typename: 'TaskList',
            id: taskListId,
            tasksCount,
            tasksCompletedCount: Math.max(tasksCompletedCount - 1, 0), // prevent negative count
          },
          errors: [] as any[],
        },
      };
    },
    onError: () => {
      showErrorToast(translate('projects.taskList.task.taskStatusUpdateError'));
    },
  });

  const [markTaskCompletedMutation] = useMutation(TASK_MARK_COMPLETE, {
    onCompleted: (data) => {
      const gqlErrors = getGqlErrors(data);
      const taskDbId = graphQLIdDecoder(taskId)?.databaseId;

      if (gqlErrors.length) {
        showErrorToast(gqlErrors[0].message);
        return;
      }
      if (taskDbId) {
        tellSteven({
          updatedAction: 'completed',
        });
      }

      if (skipOptimisticUpdate) {
        toast.success(
          translate('projects.taskList.task.taskCompleted'),
          translate('projects.taskList.task.taskCompleteUndo'),
          async () => {
            await markTaskIncompleteMutation({
              variables: {
                input: {
                  id: taskId,
                },
              },
            });
          },
          6,
        );
      }
    },
    optimisticResponse: () => {
      const { viewer } = cache.readQuery({
        query: GET_TASK_LIST_HEADER_INFO,
        variables: {
          id: taskListId,
        },
      }) as any;

      const tasksCount = get(viewer, 'taskList.tasksCount', 0);
      const tasksCompletedCount = get(
        viewer,
        'taskList.tasksCompletedCount',
        -1,
      );

      return {
        __typename: 'Mutation',
        taskMarkComplete: {
          __typename: 'TaskMarkCompletePayload',
          task: {
            id: taskId,
            __typename: 'Task',
            completed: !skipOptimisticUpdate,
          },
          taskList: {
            __typename: 'TaskList',
            id: taskListId,
            tasksCount,
            tasksCompletedCount: tasksCompletedCount + 1, // Will be 0 if taskList.taskCompletedCount is null
          },
          errors: [] as any[],
        },
      };
    },
    onError: () => {
      showErrorToast(translate('projects.taskList.task.taskStatusUpdateError'));
    },
  });

  const updateTaskStatus = async (task: { id: string; completed: boolean }) => {
    if (template || !task.id) return;

    if (!task.completed) {
      await markTaskCompletedMutation({
        variables: {
          input: {
            id: task.id,
          },
        },
      });
    } else {
      await markTaskIncompleteMutation({
        variables: {
          input: {
            id: task.id,
          },
        },
      });
    }
  };

  return {
    toggleTaskStatus: updateTaskStatus,
  };
};

export default useTaskStatus;
